import React, { useContext, useMemo } from 'react'

import { preferredDomainForPath } from 'shared/components/common/link'

type PageDomainOverride = {
  domain: string
  pageRoute?: string | null
  userCreated?: boolean
  pendingVerification?: boolean
}

export type PageDomainContextType = {
  host: string
  pathname: string
  pageDomainOverrides: Map<string, string>
  overriddenDomains: Set<string>
  rootDomain: string
}

export const PageDomainContext = React.createContext<PageDomainContextType>({
  host: '',
  pathname: '',
  pageDomainOverrides: new Map<string, string>(),
  overriddenDomains: new Set<string>(),
  rootDomain: ''
})

export type Props = {
  domains: Array<PageDomainOverride>
  host: string
  pathname: string
}

const routeGroups: { [key: string]: string[] } = {
  // The /order and /menu routes share a route group so all ordering-related
  // routes stay on the same domain.
  '/order': ['/checkout', '/confirm', '/account'],
  '/menu': ['/checkout', '/confirm', '/account']
}

/**
 * Obtain root domain
 * Prioritize in this order:
 * 1. user created homepage domain
 * 2. toast site domain (*.toast.site)
 * 3. host domain
 */
const getRootDomain = (domains: PageDomainOverride[], host: string): string => {
  const userCreatedHomepageDomain = domains?.find(
    (d) =>
      d !== null &&
      d.userCreated === true &&
      d.pendingVerification === false &&
      !d.pageRoute
  )?.domain

  const defaultSiteHomepageDomain = domains?.find(
    (d) =>
      d !== null && d.userCreated === false && d.domain.endsWith('toast.site')
  )?.domain

  const hostRootDomain = host
    .split('.')
    .reverse()
    .splice(0, 2)
    .reverse()
    .join('.')
  const rootDomain = domains.find((d) => d.domain === hostRootDomain)
    ? hostRootDomain
    : host

  return userCreatedHomepageDomain ?? defaultSiteHomepageDomain ?? rootDomain
}

export const PageDomainProvider = ({
  domains,
  host,
  pathname,
  children
}: React.PropsWithChildren<Props>) => {
  const rootDomain = useMemo(
    () => getRootDomain(domains, host),
    [domains, host]
  )
  const pageDomainOverrides = new Map<string, string>()
  const overriddenDomains = new Set<string>()
  domains.forEach((d) => {
    if (d.domain && d.pageRoute) {
      // Apply this domain override only if the user is on the override's domain,
      // or is not currently browsing the override's route on a different domain.
      // Otherwise, redirecting them to the override's domain mid-flow would lose cookies/session.
      // e.g. user is at site.com/order and order.site.com owns /order — keep them on site.com.
      const currentHostMatchesOverride = host === d.domain
      const currentPathMatchesOverrideRoute =
        pathname === d.pageRoute || pathname.startsWith(d.pageRoute + '/')
      const currentPathMatchesOverrideChildRoute =
        routeGroups[d.pageRoute]?.some(
          (page) => pathname === page || pathname.startsWith(page + '/')
        ) ?? false

      if (
        currentHostMatchesOverride ||
        (!currentPathMatchesOverrideRoute &&
          !currentPathMatchesOverrideChildRoute)
      ) {
        pageDomainOverrides.set(d.pageRoute, d.domain)
        overriddenDomains.add(d.domain)

        // Multiple route groups can share child routes (e.g. /order and /menu both claim /checkout).
        // Only let the override claim those child routes if the user isn't already on a competing
        // route group — otherwise they'd be redirected to a different domain and lose their cart.
        const currentPathBelongsToDifferentRouteGroup = Object.keys(
          routeGroups
        ).some(
          (routeGroup) =>
            (pathname === routeGroup ||
              pathname.startsWith(routeGroup + '/')) &&
            routeGroup !== d.pageRoute
        )

        if (
          currentHostMatchesOverride ||
          !currentPathBelongsToDifferentRouteGroup
        ) {
          routeGroups[d.pageRoute]?.forEach((page) =>
            pageDomainOverrides.set(page, d.domain)
          )
        }
      }
    }
  })

  return (
    <PageDomainContext.Provider
      value={{
        host,
        pathname,
        pageDomainOverrides,
        overriddenDomains,
        rootDomain
      }}
    >
      {children}
    </PageDomainContext.Provider>
  )
}

export const applyURLDomainOverride = (
  urlStr: string,
  pageDomainOverrides: Map<string, string>
): string => {
  if (pageDomainOverrides.size === 0) {
    return urlStr
  }
  let url: URL
  try {
    url = new URL(urlStr)
  } catch {
    return urlStr
  }
  const preferredDomain = preferredDomainForPath(
    url.pathname,
    pageDomainOverrides
  )
  if (!preferredDomain) {
    return urlStr
  }

  // Substitute the overriding domain in the URL.
  url.hostname = preferredDomain
  return url.toString()
}

export const usePageDomains = () => {
  return useContext(PageDomainContext)
}
